import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES, SERVICE_AREAS } from "@/mock";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-brand-dark text-brand-bg pt-24 pb-10 mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Massive CTA */}
        <div className="border-b border-white/10 pb-16 mb-16 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cta mb-4">
              Ready to start?
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Let's build something <br />
              <span className="italic font-light">you'll love living in.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link
              to="/contact"
              data-testid="footer-cta-quote"
              className="inline-flex items-center justify-between gap-3 px-6 py-4 bg-brand-cta hover:bg-brand-ctaHover text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors"
            >
              Get a Free Quote
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <a
              href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
              data-testid="footer-cta-call"
              className="inline-flex items-center justify-between gap-3 px-6 py-4 border border-white/20 hover:border-brand-cta text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors"
            >
              {COMPANY.phone}
              <Phone className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 bg-brand-cta flex items-center justify-center">
                <span className="font-heading text-brand-bg font-black text-lg leading-none">
                  J
                </span>
              </div>
              <div>
                <div className="font-heading font-black text-xl tracking-tight">
                  {COMPANY.shortName}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-0.5">
                  Contracting
                </div>
              </div>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Family-run residential remodelers. Licensed, insured, and obsessed
              with doing things the right way.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-5">
              Navigate
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="text-sm text-white/80 hover:text-brand-cta transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sm text-white/80 hover:text-brand-cta transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-5">
              Get in touch
            </h4>
            <ul className="space-y-4 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-brand-cta shrink-0" />
                <a
                  href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-brand-cta transition"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-brand-cta shrink-0" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="hover:text-brand-cta transition"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-brand-cta shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-brand-cta shrink-0" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Service Areas */}
        <div className="border-t border-white/10 pt-10 mb-10">
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-5">
            Service Areas
          </h4>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="text-sm text-white/70">
                {a}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
            Licensed & Insured.
          </p>
          <p className="text-xs text-white/40">
            Built with care · NYC + NJ
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
